import { useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * Hook to gate actions behind auth for guest users.
 * Returns { requireAuth, gateProps } where:
 *   - requireAuth(action, callback) checks if user is logged in, if not opens AuthGateSheet
 *   - gateProps = { isOpen, onClose, action, onLogin } to spread into <AuthGateSheet />
 */
export function useAuthGate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [action, setAction] = useState(null);
  const [pendingCallback, setPendingCallback] = useState(null);

  const requireAuth = useCallback((actionName, callback) => {
    if (user) {
      callback();
      return;
    }
    setAction(actionName);
    setPendingCallback(() => callback);
    setIsOpen(true);
  }, [user]);

  const onClose = useCallback(() => {
    setIsOpen(false);
    setAction(null);
    setPendingCallback(null);
  }, []);

  const onLogin = useCallback(() => {
    setIsOpen(false);
    navigate("/onboarding");
  }, [navigate]);

  return {
    requireAuth,
    gateProps: { isOpen, onClose, action, onLogin },
  };
}
